import { forwardRef } from 'react';
import type { ButtonHTMLAttributes, HTMLAttributes, ReactNode } from 'react';
import { cn } from '@/lib/utils';

/**
 * Glass redesign — Phase 2 primitive.
 *
 * Lightweight rounded label. Used everywhere a small piece of
 * metadata needs a soft container: status names, due dates,
 * type chips on cards, count badges on column heads, task
 * codes (when paired with `font-mono`).
 *
 * Distinct from `TagChip` (which carries the workspace-defined
 * tag colour and remove affordance) and from shadcn's `Badge`
 * (which is opinionated about variants in a way that doesn't
 * compose well with our token system).
 *
 * Variants:
 *   - `neutral` — uses `--accent` / `--muted-foreground`. Default.
 *   - `solid`   — fully filled with `--primary`. Use for "today" /
 *                 "active" markers, drop-target indicators.
 *   - `tinted`  — pass `tint` (any CSS color); pill renders that
 *                 colour at low alpha for the background and full
 *                 saturation for the text.
 *   - `outline` — transparent fill, 1px border in `--border`.
 *
 * `size` controls vertical density. `interactive` swaps to a
 * `<button>` for click handling (used by the inline status / due
 * pickers in the task-detail rail).
 */

type PillVariant = 'neutral' | 'solid' | 'tinted' | 'outline';
type PillSize = 'xs' | 'sm';

interface PillCommonProps {
  variant?: PillVariant;
  size?: PillSize;
  /** Required when `variant === 'tinted'`; ignored otherwise. */
  tint?: string;
  className?: string;
  children?: ReactNode;
}

type PillSpanProps = PillCommonProps & {
  interactive?: false;
} & Omit<HTMLAttributes<HTMLSpanElement>, 'className' | 'children'>;

type PillButtonProps = PillCommonProps & {
  interactive: true;
} & Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className' | 'children'>;

export type PillProps = PillSpanProps | PillButtonProps;

const baseClasses =
  'inline-flex items-center gap-1 rounded-full font-medium tracking-tight transition-colors duration-100';

const sizeClasses: Record<PillSize, string> = {
  // Padding tuned to keep the pill comfortably round at the chosen
  // text size. `font-mono` (e.g. for task codes) renders narrower so
  // we don't over-tighten.
  xs: 'h-[18px] px-1.5 text-[10.5px]',
  sm: 'h-5 px-2 text-[11.5px]',
};

const variantClasses: Record<PillVariant, string> = {
  neutral: 'bg-accent text-foreground-muted',
  solid: 'bg-primary text-primary-foreground',
  tinted: '', // computed inline from `tint`
  outline: 'border border-border bg-transparent text-foreground-muted',
};

const interactiveClasses =
  'cursor-pointer hover:text-foreground hover:bg-[color-mix(in_oklab,var(--accent)_70%,var(--background))] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-accent)]';

export const Pill = forwardRef<HTMLElement, PillProps>((props, ref) => {
  const { variant = 'neutral', size = 'sm', tint, className, children, ...rest } = props;
  const interactive = (rest as { interactive?: boolean }).interactive === true;

  const style: Record<string, string> | undefined =
    variant === 'tinted' && tint
      ? {
          background: `color-mix(in oklab, ${tint} 18%, transparent)`,
          color: tint,
        }
      : undefined;

  const cls = cn(
    baseClasses,
    sizeClasses[size],
    variantClasses[variant],
    interactive && interactiveClasses,
    className,
  );

  if (interactive) {
    const { interactive: _ignored, ...buttonRest } =
      rest as ButtonHTMLAttributes<HTMLButtonElement> & { interactive?: boolean };
    return (
      <button
        ref={ref as React.Ref<HTMLButtonElement>}
        type="button"
        className={cls}
        style={style}
        {...buttonRest}
      >
        {children}
      </button>
    );
  }

  return (
    <span
      ref={ref as React.Ref<HTMLSpanElement>}
      className={cls}
      style={style}
      {...(rest as HTMLAttributes<HTMLSpanElement>)}
    >
      {children}
    </span>
  );
});

Pill.displayName = 'Pill';
